import { useCallback, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import AdminIntake from './AdminIntake';
import AdminPartners from './AdminPartners';
import { IS_CORP } from '@/theme/adminTheme';
import { Inbox, Handshake } from 'lucide-react';

/* Partners — one page, two tabs.
   ---------------------------------------------------------------------------
   These used to be two separate sidebar entries (/admin/intake and
   /admin/partners) that looked like duplicates but aren't:

     Applications — INBOUND. What people submitted through the public /partner
                    form. A queue you review.
     Roster       — OUTBOUND. The partner list you curate yourself (free zones,
                    law firms, banks) with commission rates and status.

   Both are still their own routes, so old links and bookmarks keep working. This
   page renders the shell once via AdminLayout and mounts each of them in
   `embedded` mode, which suppresses their own shell. Nothing about their data
   handling changed.

   The switcher is the shared `.tabs` segmented control from adminShell.css — the
   same one Requests, Notifications and the pricing editors use. It used to be a
   pair of bespoke two-line pills that existed on this page alone, so walking in
   from any other admin page the tab bar visibly changed shape. The line each
   pill carried as a hint now rides under the page title, where it can be a full
   sentence and doesn't have to fit inside a button. */

type Tab = 'applications' | 'roster';

const TABS: { key: Tab; label: string; icon: typeof Inbox; blurb: string }[] = [
  {
    key: 'applications',
    label: IS_CORP ? 'Applications' : 'Applications & intakes',
    icon: Inbox,
    blurb: 'Submitted through the public form — the inbound queue you review.',
  },
  {
    key: 'roster',
    label: 'Partner roster',
    icon: Handshake,
    blurb: 'The list you curate yourself — free zones, law firms, banks and referral partners.',
  },
];

export default function AdminPartnersHub() {
  const [tab, setTab] = useState<Tab>('applications');

  /* Counts come from the children's own fetches rather than a third request of
     our own. Both stay mounted (the inactive one hidden) so each badge is right
     from first paint and switching tabs keeps search text and scroll position.
     The setters are stable and no-op on an unchanged value, so a child that
     reports on every render can't drive a loop. */
  const [counts, setCounts] = useState<Partial<Record<Tab, number>>>({});
  const onApplications = useCallback(
    (n: number) => setCounts(c => (c.applications === n ? c : { ...c, applications: n })), []);
  const onRoster = useCallback(
    (n: number) => setCounts(c => (c.roster === n ? c : { ...c, roster: n })), []);

  const current = TABS.find(t => t.key === tab)!;

  return (
    <AdminLayout section="Overview" title="Partners" icon={Handshake} subtitle={current.blurb}>
      <div className="tabs" style={{ marginBottom: 22 }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`tab${tab === key ? ' active' : ''}`}
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
          >
            <Icon size={15} strokeWidth={2.2} />
            {label}
            {counts[key] !== undefined && <span className="cnt">{counts[key]}</span>}
          </button>
        ))}
      </div>

      <div style={{ display: tab === 'applications' ? undefined : 'none' }}>
        <AdminIntake embedded onCount={onApplications} />
      </div>
      <div style={{ display: tab === 'roster' ? undefined : 'none' }}>
        <AdminPartners embedded onCount={onRoster} />
      </div>
    </AdminLayout>
  );
}
